import React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { FdsRecipientState, ListCheckResult } from "@kkp/api-client";
import { FdsInactiveBanner, useCapabilities } from "@kkp/features";
import { getFdsRecipients, recordKyc, revokeKyc } from "../../api/fdsSettings";
import { ApiError } from "../../api/client";
import { Button, Card, CardBody, CardHeader, CardTitle, toast } from "@kkp/ui";
import { formatIdr } from "@kkp/features";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../ui/Dialog";
import { Field } from "../../ui/Input";

export const FDS_RECIPIENTS_KEY = (companyId: string) => [
  "fds-recipients",
  companyId,
];

// eslint-disable-next-line react-refresh/only-export-components -- shared with FdsBaseline.tsx's roster builder
export function useFdsRecipients(companyId: string) {
  return useQuery({
    queryKey: FDS_RECIPIENTS_KEY(companyId),
    queryFn: () => getFdsRecipients(companyId),
    enabled: Boolean(companyId),
    refetchOnMount: "always",
  });
}

type DialogState =
  | { mode: "record"; recipient: FdsRecipientState }
  | { mode: "revoke"; recipient: FdsRecipientState }
  | null;

const RECORD_KYC_DEFAULTS = {
  evidenceRef: "",
  listCheckSource: "",
  listCheckVersion: "",
  listCheckResult: "Clear" as ListCheckResult,
};

export function FdsRecipientsSection({ companyId }: { companyId: string }) {
  const queryClient = useQueryClient();
  const { data: capabilities } = useCapabilities();
  const fdsEnabled = capabilities?.fds === true;
  const { data, isLoading, isError } = useFdsRecipients(companyId);
  const [dialog, setDialog] = React.useState<DialogState>(null);

  const closeDialog = () => setDialog(null);
  const onSaved = () => {
    closeDialog();
    void queryClient.invalidateQueries({
      queryKey: FDS_RECIPIENTS_KEY(companyId),
    });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle as="h2">Recipient KYC &amp; list check</CardTitle>
      </CardHeader>

      <CardBody className="grid gap-4">
        <FdsInactiveBanner fds={capabilities?.fds} />
        {isError ? (
          <div className="text-sm text-[color:var(--kkp-text-muted)]">
            Failed to load recipients.
          </div>
        ) : null}

        {isLoading ? (
          <div className="text-sm text-[color:var(--kkp-text-muted)]">
            Loading…
          </div>
        ) : null}

        {data ? (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b border-[color:var(--kkp-border)] text-left text-xs text-[color:var(--kkp-text-muted)]">
                  <th className="py-2 pr-3">Name</th>
                  <th className="py-2 pr-3">Employee ID</th>
                  <th className="py-2 pr-3">Bank</th>
                  <th className="py-2 pr-3 text-right">Approved principal</th>
                  <th className="py-2 pr-3">KYC state</th>
                  <th className="py-2 pr-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {data.map((r) => (
                  <tr
                    key={r.seqId}
                    className="border-b border-[color:var(--kkp-border)] text-sm text-[color:var(--kkp-text)]"
                  >
                    <td className="py-2 pr-3">{r.fullName}</td>
                    <td className="py-2 pr-3">{r.employeeId}</td>
                    <td className="py-2 pr-3">
                      {r.bankCode} · {r.bankAccount}
                    </td>
                    <td className="py-2 pr-3 text-right">
                      {r.approvedPrincipal
                        ? formatIdr(r.approvedPrincipal)
                        : "—"}
                    </td>
                    <td className="py-2 pr-3">
                      <KycState kyc={r.kyc} />
                    </td>
                    <td className="py-2 pr-3 text-right">
                      {r.kyc ? (
                        <Button
                          variant="secondary"
                          size="sm"
                          disabled={!fdsEnabled}
                          onClick={() =>
                            setDialog({ mode: "revoke", recipient: r })
                          }
                        >
                          Revoke KYC
                        </Button>
                      ) : (
                        <Button
                          variant="primary"
                          size="sm"
                          disabled={!fdsEnabled}
                          onClick={() =>
                            setDialog({ mode: "record", recipient: r })
                          }
                        >
                          Record KYC
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
                {data.length === 0 ? (
                  <tr>
                    <td
                      colSpan={6}
                      className="py-3 text-center text-sm text-[color:var(--kkp-text-muted)]"
                    >
                      No recipients found.
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        ) : null}
      </CardBody>

      <Dialog
        open={dialog !== null}
        onOpenChange={(open) => {
          if (!open) closeDialog();
        }}
      >
        <DialogContent>
          {dialog?.mode === "record" ? (
            <RecordKycDialog
              companyId={companyId}
              recipient={dialog.recipient}
              fdsEnabled={fdsEnabled}
              onClose={closeDialog}
              onSaved={onSaved}
            />
          ) : null}
          {dialog?.mode === "revoke" ? (
            <RevokeKycDialog
              companyId={companyId}
              recipient={dialog.recipient}
              fdsEnabled={fdsEnabled}
              onClose={closeDialog}
              onSaved={onSaved}
            />
          ) : null}
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function KycState({ kyc }: { kyc: FdsRecipientState["kyc"] }) {
  if (!kyc) {
    return (
      <span className="text-[color:var(--kkp-text-muted)]">Not approved</span>
    );
  }
  if (kyc.listCheckResult === "PotentialMatch") {
    return (
      <div className="flex items-center gap-2">
        <span>Approved</span>
        <span className="inline-flex items-center rounded-full border border-[color:rgba(245,158,11,0.45)] bg-[color:rgba(245,158,11,0.12)] px-2 py-0.5 text-[11px] font-bold text-white">
          Compliance review
        </span>
      </div>
    );
  }
  return <span>Approved · Clear</span>;
}

const inputClassName =
  "w-full rounded-xl border border-[color:rgba(255,255,255,0.12)] bg-[color:rgba(255,255,255,0.06)] px-3 py-2.5 text-[color:rgba(255,255,255,0.92)] placeholder:text-[color:rgba(255,255,255,0.35)] outline-none transition focus:border-[color:color-mix(in_oklab,var(--kkp-magenta)_70%,white)] focus:shadow-[0_0_0_3px_rgba(203,72,226,0.18)]";

function RecordKycDialog({
  companyId,
  recipient,
  fdsEnabled,
  onClose,
  onSaved,
}: {
  companyId: string;
  recipient: FdsRecipientState;
  fdsEnabled: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [form, setForm] = React.useState(RECORD_KYC_DEFAULTS);
  const [formError, setFormError] = React.useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: () =>
      recordKyc(companyId, recipient.seqId, {
        evidenceRef: form.evidenceRef.trim(),
        listCheckSource: form.listCheckSource.trim(),
        listCheckVersion: form.listCheckVersion.trim(),
        listCheckResult: form.listCheckResult,
      }),
    onSuccess: () => {
      toast.success("KYC approval recorded.", { title: "Saved" });
      onSaved();
    },
    onError: (err: unknown) => {
      const message =
        err instanceof ApiError
          ? err.message
          : "Failed to record KYC approval.";
      setFormError(message);
      toast.danger(message, { title: "Error" });
    },
  });

  const isValid =
    form.evidenceRef.trim() !== "" &&
    form.listCheckSource.trim() !== "" &&
    form.listCheckVersion.trim() !== "";

  return (
    <>
      <DialogHeader>
        <DialogTitle>Record KYC — {recipient.fullName}</DialogTitle>
        <DialogDescription>
          List-check result and the back-office evidence behind it.
        </DialogDescription>
      </DialogHeader>

      <div className="grid gap-3">
        <Field label="Evidence ref" required>
          <input
            className={inputClassName}
            placeholder="e.g. BO-015"
            value={form.evidenceRef}
            onChange={(e) =>
              setForm((f) => ({ ...f, evidenceRef: e.target.value }))
            }
          />
        </Field>
        <Field label="List source" required>
          <input
            className={inputClassName}
            placeholder="e.g. DTTOT"
            value={form.listCheckSource}
            onChange={(e) =>
              setForm((f) => ({ ...f, listCheckSource: e.target.value }))
            }
          />
        </Field>
        <Field label="List version / date" required>
          <input
            className={inputClassName}
            placeholder="e.g. 2026-09-01"
            value={form.listCheckVersion}
            onChange={(e) =>
              setForm((f) => ({ ...f, listCheckVersion: e.target.value }))
            }
          />
        </Field>
        <Field label="Result" required>
          <select
            className={inputClassName}
            value={form.listCheckResult}
            onChange={(e) =>
              setForm((f) => ({
                ...f,
                listCheckResult: e.target.value as ListCheckResult,
              }))
            }
          >
            <option value="Clear">Clear</option>
            <option value="PotentialMatch">Potential match</option>
          </select>
        </Field>

        {formError ? (
          <div className="text-xs font-semibold text-[color:rgba(239,68,68,0.95)]">
            {formError}
          </div>
        ) : null}
      </div>

      <DialogFooter>
        <Button
          variant="ghost"
          type="button"
          onClick={onClose}
          disabled={mutation.isPending}
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          type="button"
          disabled={!fdsEnabled || !isValid || mutation.isPending}
          onClick={() => {
            setFormError(null);
            mutation.mutate();
          }}
        >
          {mutation.isPending ? "Saving…" : "Save"}
        </Button>
      </DialogFooter>
    </>
  );
}

function RevokeKycDialog({
  companyId,
  recipient,
  fdsEnabled,
  onClose,
  onSaved,
}: {
  companyId: string;
  recipient: FdsRecipientState;
  fdsEnabled: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [reason, setReason] = React.useState("");
  const [evidenceRef, setEvidenceRef] = React.useState("");

  const mutation = useMutation({
    mutationFn: () =>
      revokeKyc(companyId, recipient.seqId, {
        reason: reason.trim(),
        evidenceRef: evidenceRef.trim(),
      }),
    onSuccess: () => {
      toast.success("KYC approval revoked.", { title: "Saved" });
      onSaved();
    },
    onError: (err: unknown) => {
      const message =
        err instanceof ApiError
          ? err.message
          : "Failed to revoke KYC approval.";
      toast.danger(message, { title: "Error" });
    },
  });

  const isValid = reason.trim() !== "" && evidenceRef.trim() !== "";

  return (
    <>
      <DialogHeader>
        <DialogTitle>Revoke KYC — {recipient.fullName}</DialogTitle>
        <DialogDescription>
          Ends the current approval; batches pending for this recipient are put
          on hold.
        </DialogDescription>
      </DialogHeader>

      <div className="grid gap-3">
        <Field label="Reason" required>
          <textarea
            rows={3}
            className={inputClassName}
            placeholder="Required — why is this approval being revoked?"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </Field>
        <Field label="Evidence ref" required>
          <input
            className={inputClassName}
            placeholder="e.g. BO-016"
            value={evidenceRef}
            onChange={(e) => setEvidenceRef(e.target.value)}
          />
        </Field>
      </div>

      <DialogFooter>
        <Button
          variant="ghost"
          type="button"
          onClick={onClose}
          disabled={mutation.isPending}
        >
          Cancel
        </Button>
        <Button
          variant="danger"
          type="button"
          disabled={!fdsEnabled || !isValid || mutation.isPending}
          onClick={() => mutation.mutate()}
        >
          {mutation.isPending ? "Saving…" : "Revoke"}
        </Button>
      </DialogFooter>
    </>
  );
}
